import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Loader2, Sparkles } from 'lucide-react';
import api from '../lib/api';

interface OnboardingWizardProps {
  classId: number;
  onComplete: (newStudentId: number) => void;
}

type AgeRange = '3-5' | '6-9' | '10+';

const AGE_RANGES: AgeRange[] = ['3-5', '6-9', '10+'];

// 注册成功后的首次孩子建立流程：
//   1. 收集姓名 + age_range
//   2. POST /students 创建
//   3. 按 age_range 一键导入打卡 / 行为习惯 / 小卖部
//   4. 通过 onComplete 把新建的 studentId 抛回 Home 让其开宠物选择弹窗
export default function OnboardingWizard({ classId, onComplete }: OnboardingWizardProps) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [ageRange, setAgeRange] = useState<AgeRange>('6-9');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      setError(t('onboarding.errors.nameRequired'));
      return;
    }
    if (submitting) return;
    setSubmitting(true);
    setError('');
    try {
      const res = await api.post('/students', {
        name: trimmed,
        classId,
        ageRange,
      });
      const newStudentId: number | undefined = res.data?.id;

      // 三套预置并行导入；单个失败不阻塞整体。
      await Promise.allSettled([
        api.post('/point-items/import-system-template', { toClassId: classId, ageRange }),
        api.post('/checkin/tasks/import-system-template', { toClassId: classId, ageRange }),
        api.post('/goods/import-system-template', { toClassId: classId, ageRange }),
      ]);

      if (newStudentId) {
        onComplete(newStudentId);
      }
    } catch (err) {
      console.error('Onboarding failed', err);
      setError(t('onboarding.errors.createFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] text-center animate-in fade-in zoom-in-95 duration-500 px-4">
      <div className="w-20 h-20 bg-pink-50 rounded-full flex items-center justify-center mb-6">
        <Sparkles className="w-10 h-10 text-pink-400" />
      </div>
      <h2 className="text-2xl font-bold text-gray-800 mb-2">{t('onboarding.title')}</h2>
      <p className="text-gray-400 mb-6 max-w-sm">{t('onboarding.subtitle')}</p>

      <div className="w-full max-w-sm bg-white rounded-3xl shadow-xl shadow-pink-100 border border-pink-50 p-6 space-y-5">
        <div className="text-left space-y-2">
          <label className="text-sm font-bold text-slate-700">{t('onboarding.nameLabel')}</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t('onboarding.namePlaceholder')}
            maxLength={20}
            disabled={submitting}
            className="block w-full px-4 py-3 border border-slate-200 rounded-2xl bg-white/90 focus:ring-4 focus:ring-pink-100 focus:border-pink-300 outline-none transition-all text-sm"
          />
        </div>

        <div className="text-left space-y-2">
          <label className="text-sm font-bold text-slate-700">{t('onboarding.ageRangeLabel')}</label>
          <p className="text-xs text-slate-400">{t('onboarding.ageRangeHint')}</p>
          <div className="grid grid-cols-3 gap-2">
            {AGE_RANGES.map((range) => (
              <button
                key={range}
                type="button"
                onClick={() => setAgeRange(range)}
                disabled={submitting}
                className={`py-2.5 rounded-2xl text-sm font-bold transition-all border ${
                  ageRange === range
                    ? 'bg-pink-50 border-pink-300 text-pink-600'
                    : 'bg-white border-slate-200 text-slate-500 hover:bg-slate-50'
                } ${submitting ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {t(`onboarding.ageRange_${range.replace('+', 'plus')}`)}
              </button>
            ))}
          </div>
        </div>

        {error && (
          <div className="bg-rose-50 text-rose-600 text-xs p-3 rounded-2xl border border-rose-100 text-left">
            {error}
          </div>
        )}

        <button
          onClick={handleSubmit}
          disabled={submitting || !name.trim()}
          className="w-full flex items-center justify-center gap-2 py-3 px-4 rounded-2xl text-sm font-extrabold text-white bg-gradient-to-r from-[#ff4da6] via-[#ff7a59] to-[#ffd166] shadow-lg shadow-pink-200 hover:brightness-105 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
        >
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-5 h-5" />}
          {submitting ? t('onboarding.creating') : t('onboarding.create')}
        </button>
      </div>
    </div>
  );
}
